import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

import { resourceScopeCleared, userScopeCleared } from '@krp/client-runtime'

import type { CommandStatus } from '../../../shared/model/commandOutcome'
import { ownedBy } from '../../../shared/model/scopeKeys'
import type { ErrorView } from '@krp/client-runtime'
import { photoProductKey } from './keys'
import type { PhotoOutcome } from './outcomes'
import type { PhotoOperation, PhotoPhase, PhotosIntentState } from './types'

/**
 * Намерение в окне фото: какой кадр смотрит человек и пишущие операции
 * (главное фото, загрузка). Списка кадров здесь нет — он в кэше (R01).
 * Срок — `screen`: байты загрузки живут только в памяти страницы, и после
 * перезагрузки команду загрузки всё равно нечем повторить.
 */
const initialState: PhotosIntentState = { selected: {}, operations: {} }

const photosSlice = createSlice({
  name: 'onec/photos',
  initialState,
  reducers: {
    photoSelected(state, action: PayloadAction<{ resourceId: string; refKey: string; fileKey: string }>) {
      state.selected[photoProductKey(action.payload.resourceId, action.payload.refKey)] = action.payload.fileKey
    },
    photoOperationStarted(state, action: PayloadAction<PhotoOperation>) {
      const current = state.operations[action.payload.key]
      if (current !== undefined && (current.status === 'sending' || current.status === 'unknown')) return
      state.operations[action.payload.key] = action.payload
    },
    photoOperationSettled(
      state,
      action: PayloadAction<{
        key: string
        /** Итог пишется только своей операции: очищенная или заменённая не воскресает. */
        operationId: string
        status: Exclude<CommandStatus, 'sending'>
        resendable: boolean
        error: ErrorView | null
        outcome: PhotoOutcome | null
      }>,
    ) {
      const operation = state.operations[action.payload.key]
      if (operation === undefined || operation.operationId !== action.payload.operationId) return
      operation.status = action.payload.status
      operation.phase = null
      operation.resendable = action.payload.resendable
      operation.error = action.payload.error
      operation.outcome = action.payload.outcome
    },
    /** Ход идущей загрузки: этап и реальные байты. Опоздавшее сообщение о закончившейся или заменённой операции игнорируется. */
    photoOperationProgress(
      state,
      action: PayloadAction<{ key: string; operationId: string; phase: PhotoPhase; loaded: number; total: number | null }>,
    ) {
      const operation = state.operations[action.payload.key]
      if (operation === undefined || operation.operationId !== action.payload.operationId || operation.status !== 'sending') return
      operation.phase = action.payload.phase
      operation.loaded = action.payload.loaded
      operation.total = action.payload.total
    },
    photoOperationResent(state, action: PayloadAction<{ key: string }>) {
      const operation = state.operations[action.payload.key]
      if (operation === undefined) return
      operation.status = 'sending'
      operation.phase = operation.action === 'upload' ? 'sending' : null
      operation.loaded = 0
      operation.total = null
      operation.error = null
    },
    photoOperationDismissed(state, action: PayloadAction<{ key: string }>) {
      const operation = state.operations[action.payload.key]
      if (operation === undefined || operation.status === 'sending' || operation.status === 'unknown') return
      delete state.operations[action.payload.key]
    },
  },
  extraReducers: (builder) => {
    builder.addCase(userScopeCleared, () => initialState)
    builder.addCase(resourceScopeCleared, (state, action) => {
      for (const key of Object.keys(state.selected)) if (ownedBy(key, action.payload.resourceId)) delete state.selected[key]
      for (const key of Object.keys(state.operations)) {
        if (ownedBy(key, action.payload.resourceId)) delete state.operations[key]
      }
    })
  },
})

export const {
  photoSelected,
  photoOperationStarted,
  photoOperationSettled,
  photoOperationProgress,
  photoOperationResent,
  photoOperationDismissed,
} = photosSlice.actions
export const photosReducer = photosSlice.reducer
